import { useMemo } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import { Permission } from '@/graphql/types';
import Button from '../../form/Button';
import LoadingSpinner from '../../loadingSpinner/LoadingSpinner';
import Tooltip from '../../../utils/tooltip';
import { BasePage } from '../BasePage';
import { AttributesOverviewTable } from './components/AttributesOverviewTable';
import type { AttributeEntityType, AttributeEntry } from './contracts';
import { formatAttributeEntityType } from './contracts';
import { useAttributesData } from './graphql/useAttributesData';
import { createAttributeNewPath } from './routeUtils';

interface AttributesPageProps {
    entityType: AttributeEntityType;
    entityId: string;
}

export interface AttributesPageOutletContext {
    entityType: AttributeEntityType;
    entityId: string;
    entries: AttributeEntry[];
    loading: boolean;
    canEditAttributes: boolean;
    refetchAttributes: () => Promise<void>;
}

export function useAttributesPageContext(): AttributesPageOutletContext {
    return useOutletContext<AttributesPageOutletContext>();
}

export function AttributesPage({ entityType, entityId }: AttributesPageProps) {
    const { entries, entityLabel, entityPermission, loading, error, refetch } = useAttributesData({
        entityType,
        entityId,
    });
    const canEditAttributes = entityType === 'user' || entityPermission === Permission.FullAccess;
    const contextValue = useMemo<AttributesPageOutletContext>(
        () => ({
            entityType,
            entityId,
            entries,
            loading,
            canEditAttributes,
            refetchAttributes: async () => {
                await refetch();
            },
        }),
        [canEditAttributes, entityId, entityType, entries, loading, refetch]
    );
    const entityTypeLabel = formatAttributeEntityType(entityType);

    function renderTable() {
        if (error) {
            return (
                <p className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    {gettext('ATTRIBUTES_LOAD_ERROR')}
                </p>
            );
        }

        if (!loading && entries.length === 0) {
            return (
                <p className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500">
                    {gettext('ATTRIBUTES_EMPTY_STATE')}
                </p>
            );
        }

        return (
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="flex flex-col gap-3 border-b border-gray-100 px-6 py-4 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800">
                            {gettext('ATTRIBUTES_PAGE_TITLE')}
                        </h2>
                        <p className="text-sm text-gray-500">
                            {gettext('ATTRIBUTES_TABLE_DESCRIPTION')}
                        </p>
                    </div>
                    {loading && (
                        <div
                            className="flex items-center gap-2 text-sm text-gray-500"
                            role="status"
                        >
                            <LoadingSpinner className="h-4 text-gray-400" />
                            <span>{gettext('ATTRIBUTES_LOADING')}</span>
                        </div>
                    )}
                </div>
                <AttributesOverviewTable
                    entityType={entityType}
                    entityId={entityId}
                    entries={entries}
                    loading={loading && entries.length === 0}
                    canEdit={canEditAttributes}
                />
            </div>
        );
    }

    return (
        <BasePage
            title={gettext('ATTRIBUTES_PAGE_TITLE')}
            besideTitle={
                canEditAttributes ? (
                    <Button
                        to={createAttributeNewPath(entityType, entityId)}
                        state={{ fromAttributesOverview: true }}
                        variant="primary"
                        className="inline-flex px-6 py-2 text-sm"
                    >
                        {gettext('ATTRIBUTE_CREATE_ACTION')}
                    </Button>
                ) : (
                    <Button
                        type="button"
                        variant="primary"
                        className="cursor-not-allowed px-6 py-2 text-sm opacity-50"
                        disabled
                    >
                        {gettext('ATTRIBUTE_CREATE_ACTION')}
                    </Button>
                )
            }
        >
            <div className="space-y-6">
                <section className="grid gap-3 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm md:grid-cols-3">
                    <div>
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                            {gettext('ATTRIBUTE_ENTITY_TYPE_LABEL')}
                        </span>
                        <p className="mt-1 text-sm font-semibold text-gray-800">
                            {entityTypeLabel}
                        </p>
                    </div>
                    <div className="min-w-0">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                            {gettext('ATTRIBUTE_ENTITY_LABEL')}
                        </span>
                        <Tooltip content={entityLabel}>
                            <p className="mt-1 truncate text-sm font-semibold text-gray-800">
                                {entityLabel}
                            </p>
                        </Tooltip>
                    </div>
                    <div>
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                            {gettext('ATTRIBUTE_ENTITY_ID_LABEL')}
                        </span>
                        <p className="mt-1 text-sm font-semibold text-gray-800">{entityId}</p>
                    </div>
                </section>

                {renderTable()}
                <Outlet context={contextValue} />
            </div>
        </BasePage>
    );
}
